import css from "./legal.module.css";

/**
 * Privacy Policy — TV On Guitar LLC.
 *
 * The previous version claimed: *"We do not store personally identifiable
 * information (PII) on our infrastructure."* **That was not true.** The
 * `creators` table stores a `displayName` taken from the Patreon profile, every
 * `files` row is keyed by a person's Auth0 `sub`, and the entitlement cache
 * stores `(sub, creatorId, tierId)`. A pseudonymous identifier tied to an
 * individual is personal data under both GDPR and the CCPA.
 *
 * **Revised 2026-09-08** for `patreon-direct-auth` task 12, which removed
 * Auth0. That changed what is true here in both directions and neither could
 * be left unsaid: the account record Auth0 held — email, sign-in history,
 * approximate location — is gone entirely, and in its place we now store a
 * live Patreon access and refresh token ourselves. Less data about a person,
 * and one more credential. A policy that mentioned only the first half would
 * be flattering rather than accurate.
 *
 * That mattered enough to fix on its own terms. Adam asked for these pages to
 * reduce legal exposure, and an inaccurate privacy representation *creates*
 * exposure — a distinct one from copyright, enforceable by regulators rather
 * than rights holders, and it undermines the credibility of the rest of the
 * document in exactly the place you want to look careful.
 *
 * So this describes what the system actually does. Verified against
 * `CreatorService.ts`, `EntitlementCache.ts`, `AssetKeys.ts`,
 * `DynamoPatreonTokenStore.ts` and `PatreonIdentity.ts`, rather than
 * asserted.
 *
 * **Revised 2026-10-02** for `creator-analytics`, which started recording what
 * patrons open and download. Two sentences here said the opposite — "no
 * analytics tracking of individual users" and "nothing is written when you
 * simply browse or open a file" — and they change in the same commit as the
 * recording (CA-D18), so the live policy is never false. The wording is
 * `specs/creator-analytics/privacy-policy-draft.md`, not yet reviewed by
 * counsel. It rests on two things the code must keep true: events carry no
 * identity (`services/Analytics.ts`), and each month's salt is destroyed
 * (`AnalyticsSaltsTable`, no backups). If either changes, so must this.
 */

const EFFECTIVE = "October 2, 2026";
const CONTACT = "tvonguitar@gmail.com";
const ENTITY = "TV On Guitar LLC";

export default function PrivacyPolicyPage() {
  return (
    <div className={css.page}>
      <h1 className={css.title}>Privacy Policy</h1>
      <p className={css.meta}>Effective {EFFECTIVE}</p>

      <section className={css.section}>
        <h2 className={css.heading}>1. Introduction</h2>
        <p className={css.body}>
          This service is operated by {ENTITY} (“we”, “our”, “us”). This policy
          explains what we collect, why, and what you can do about it. We have
          tried to describe what the system actually does rather than what is
          convenient to say.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>2. How you sign in</h2>
        <p className={css.body}>
          You sign in with Patreon. We never see or store your Patreon
          password. Patreon asks you to approve access, and then tells us who
          you are and which tiers you support. There is no other sign-in
          provider involved.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>3. What is stored about you</h2>
        <p className={css.body}>
          All of it sits on our own servers. Until September 2026 a sign-in
          provider held a separate account record about you — your email, when
          you last signed in and roughly from where. That record no longer
          exists and we no longer keep anything equivalent.
        </p>
        <ul className={css.list}>
          <li>
            <strong>An account identifier.</strong> Derived from your Patreon
            user id. It is attached to files you upload and to your cached
            membership tier.
          </li>
          <li>
            <strong>Your membership tier</strong>, cached for up to five minutes
            so that every page load does not have to ask Patreon again. It
            records which tier you are on, not your payment details.
          </li>
          <li>
            <strong>A display name</strong>, for accounts that publish content —
            taken from the Patreon profile, and shown as the creator’s name.
          </li>
          <li>
            <strong>Files and their descriptions</strong>, for accounts that
            upload them.
          </li>
          <li>
            <strong>A Patreon access token</strong>, so we can ask Patreon which
            tier you are on without sending you back to sign in on every page.
            It is stored while you are signed in, deleted when you sign out, and
            expires by itself within thirty days if you never do. It is the one
            thing here that would matter if it were stolen, which is why it is
            never sent to your browser and never written to a log.
          </li>
          <li>
            <strong>A record of what was opened and downloaded.</strong> When you
            open the library, open a piece, or download a file, we record that it
            happened: which piece, which file, which membership tier, and when.
            The record does not contain your name, email, Patreon id or account
            identifier. In their place is a scrambled code that lets us count
            how many different people visited in a month, without being able to
            tell who any of them were. The code changes every month, so visits
            in different months cannot be connected to each other. Visitors who
            are not signed in are recorded only as “the landing page was
            opened”, with no code and no tier.
          </li>
        </ul>
        <p className={css.body}>
          We do <strong>not</strong> store your payment details, card numbers or
          billing address. Those stay with Patreon and we never receive them.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>4. What we read but do not keep</h2>
        <p className={css.body}>
          To work out what you are entitled to see, we read your Patreon
          membership and, for display on your account page, your name, avatar
          and email address. We ask Patreon for these each time they are shown.
          <strong> Your email address is never written to our database.</strong>
          It reaches your screen and goes no further.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>5. Why we hold it</h2>
        <p className={css.body}>
          To run the service and to understand how it is used: to know who you
          are, to show you the material your membership includes, to let
          creators manage what they have published, and to show creators how
          much their published material is opened and downloaded, as counts and
          never as names. We do not sell personal information, we do not share it
          with advertisers, and we do not use it for advertising or profiling.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>6. Third parties</h2>
        <ul className={css.list}>
          <li>
            <strong>Patreon</strong> — identity and membership. Governed by
            Patreon’s own privacy policy.
          </li>
          <li>
            <strong>Amazon Web Services</strong> — hosting, file storage and
            delivery, in the United States.
          </li>
        </ul>
        <p className={css.body}>
          No analytics service, advertising network or other third party
          receives any of it.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>7. Cookies and local storage</h2>
        <p className={css.body}>
          We use browser storage to keep you signed in and to remember small
          preferences, such as how you last sorted the library. We do not use
          advertising or third-party tracking cookies, and nothing in your
          browser is used for analytics. The usage records described in section
          3 are made on our servers, from requests your browser already sends,
          and do not identify you.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>8. Why we are allowed to hold it</h2>
        <p className={css.body}>
          Where the GDPR applies, our lawful basis is performance of a contract:
          we cannot show you the material your membership includes without
          knowing which membership you have. We do not rely on consent for this,
          and we do not process your data for any purpose beyond running the
          service.
        </p>
        <p className={css.body}>
          The usage records in section 3 are not needed to give you your
          membership, so for those we rely on legitimate interests instead:
          creators need to know whether what they publish is used, in order to
          decide what to make next. We have kept that interest as narrow as we
          can. The records contain no name, email or account identifier, the
          code that stands in for you changes monthly, and creators only ever
          see totals.
        </p>
        <p className={css.body}>
          Our servers and file storage are in the United States, and your data
          is held there. If you are in the United Kingdom or the European
          Economic Area, that is a transfer to a third country, and it is
          permitted where an approved safeguard is in place. We rely on the
          safeguards Amazon Web Services offers, which include the EU–US Data
          Privacy Framework and its UK extension, and Standard Contractual
          Clauses where those apply.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>9. Retention</h2>
        <p className={css.body}>
          <strong>On our own servers</strong>, if you are a member and do not
          publish content, the only thing stored is one cached record of your
          membership tier. It stops being used after five minutes and is deleted
          automatically; the deletion is performed by our database on its own
          schedule, which can take up to 48 hours. When you open the library,
          open a piece or download a file, a usage record is written as
          described in section 3.
        </p>
        <p className={css.body}>
          <strong>Your access token lasts longer than the cache</strong>, but
          not indefinitely: signing out deletes it immediately, and it expires
          on its own within thirty days otherwise. Ending your Patreon
          membership does not delete it — it stops you getting in, which is not
          the same thing — so if you want it gone sooner, sign out or ask us.
        </p>
        <p className={css.body}>
          <strong>Usage records are kept permanently</strong>, because they are
          a creator’s history of how their work has been used. The monthly code
          that stands in for you is itself deleted within about ten days after
          each month ends. After that, nothing we hold, including the code, can
          connect a record back to you.
        </p>
        <p className={css.body}>
          For accounts that publish content, the display name, files and their
          descriptions are kept while the account exists. Server logs are
          retained for a limited period for debugging and security.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>10. Your rights</h2>
        <p className={css.body}>
          You may ask us what we hold about you, ask for it to be corrected, or
          ask us to delete it, and you may withdraw access at any time by ending
          your Patreon membership. Email {CONTACT} and we will respond. Depending
          on where you live you may also have the right to object to processing,
          to request a copy of your data in a portable form, or to complain to
          your local data protection authority.
        </p>
        <p className={css.body}>
          If you ask us to delete your data we remove everything on our
          servers, including your stored access token. Most of it expires on its
          own — the cached tier within minutes, the token within thirty days —
          so for a member who does not publish content there is usually very
          little left to delete. Patreon holds its own copy of your membership
          and billing information under its own policy; that request has to go
          to Patreon, and we cannot make it for you.
        </p>
        <p className={css.body}>
          Usage records from the current month, and from the previous month
          until its code is deleted, can still be connected to you. On request,
          we will delete them from our permanent store. Copies held in our
          short-term reporting system cannot be removed one person at a time, and
          expire on their own within 90 days. Older usage records cannot be
          connected to anyone, including by us, so there is nothing left that
          identifies you to delete.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>11. Security</h2>
        <p className={css.body}>
          Files are stored privately and served through short-lived links.
          Access to material is checked on our servers on every request, not in
          your browser. No system is perfectly secure, and we do not claim
          otherwise.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>12. Children</h2>
        <p className={css.body}>
          This service is not directed at children under 13, and we do not
          knowingly collect their personal information.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>13. Changes and contact</h2>
        <p className={css.body}>
          We may update this policy; material changes will be reflected in the
          effective date above. Questions or requests: {ENTITY} — {CONTACT}.
        </p>
      </section>
    </div>
  );
}
